import React, { useEffect, useState } from 'react';
import API, { getSchemaName } from 'shared/api';
import { fmtMonto } from 'shared/format';
import { useAuth } from 'shared/context/AuthContext';
import s from './styles';

function isDark() {
  try {
    return document.documentElement.getAttribute('data-dark') === 'true';
  } catch {
    return false;
  }
}

const FINALIZADOS = ['entregado', 'rechazado', 'cancelado', 'credito_pagado'];
const ESTADOS_LABEL = {
  entregado: 'Entregado',
  rechazado: 'Rechazado',
  cancelado: 'Cancelado',
  credito_pagado: 'Credito pagado',
};
const ESTADOS_COLOR = {
  entregado: '#34a853',
  rechazado: '#ea4335',
  cancelado: '#9e9e9e',
  credito_pagado: '#34a853',
};

const formatearFecha = (fecha) => {
  if (!fecha) return '';
  const d = new Date(fecha);
  return (
    d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' }) +
    ' ' +
    d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
  );
};

const PedidosFinalizados = ({ filtro, setFiltro, pedidoActivo, onSelectPedido }) => {
  const [data, setData] = useState([]);
  const [cargando, setCargando] = useState(false);
  const { tieneVisibilidad } = useAuth();
  const v = (c) => tieneVisibilidad(c);

  useEffect(() => {
    const fetchData = async () => {
      setCargando(true);
      try {
        const params = new URLSearchParams();
        FINALIZADOS.forEach((e) => params.append('estado', e));
        if (filtro.desde) params.append('desde', filtro.desde);
        if (filtro.hasta) params.append('hasta', filtro.hasta);
        if (filtro.cliente) params.append('cliente', filtro.cliente);
        if (filtro.sede) params.append('codigo_sede', filtro.sede);

        const res = await API.get(`/pedidos/listar/${getSchemaName()}?${params.toString()}`);
        if (res.data.status === 'success') {
          const pedidos = res.data.data.filter((p) => FINALIZADOS.includes(p.estado));
          setData(pedidos);
        }
      } catch (e) {
        console.error('Error cargando finalizados:', e);
      } finally {
        setCargando(false);
      }
    };
    fetchData();
  }, [filtro]);

  return (
    <div style={s.finalizadosContainer}>
      <div style={s.finalizadosFilters}>
        <span style={s.finalizadosFilterLabel}>Desde</span>
        <input
          type="date"
          value={filtro.desde || ''}
          onChange={(e) => setFiltro((prev) => ({ ...prev, desde: e.target.value }))}
          style={{ ...s.finalizadosFilterInput, width: '140px' }}
        />
        <span style={s.finalizadosFilterLabel}>Hasta</span>
        <input
          type="date"
          value={filtro.hasta || ''}
          onChange={(e) => setFiltro((prev) => ({ ...prev, hasta: e.target.value }))}
          style={{ ...s.finalizadosFilterInput, width: '140px' }}
        />
        <span style={s.finalizadosFilterLabel}>Cliente</span>
        <input
          type="text"
          placeholder="Buscar cliente..."
          value={filtro.cliente || ''}
          onChange={(e) => setFiltro((prev) => ({ ...prev, cliente: e.target.value }))}
          style={{ ...s.finalizadosFilterInput, width: '180px' }}
        />
        {cargando && (
          <span style={{ fontSize: '11px', color: 'var(--agx-pri2)' }}>Cargando...</span>
        )}
      </div>

      <div style={s.finalizadosTable}>
        <div style={s.finalizadosTableHead}>
          <span style={{ ...s.finalizadosCell, width: '60px' }}>ID</span>
          <span style={{ ...s.finalizadosCell, flex: 1, minWidth: '130px' }}>Cliente</span>
          <span style={{ ...s.finalizadosCell, width: '120px' }}>Sede</span>
          <span style={{ ...s.finalizadosCell, width: '160px' }}>Fecha</span>
          <span style={{ ...s.finalizadosCell, width: '100px' }}>Total</span>
          <span style={{ ...s.finalizadosCell, width: '110px' }}>Estado</span>
        </div>

        {data.length === 0 && (
          <div
            style={{
              textAlign: 'center',
              color: 'var(--agx-faint)',
              fontSize: '13px',
              padding: '40px 20px',
            }}
          >
            {cargando ? 'Cargando...' : 'No hay pedidos finalizados'}
          </div>
        )}

        {data.map((p) => (
          <div
            key={p.id}
            onClick={() => onSelectPedido(p)}
            style={{
              ...s.finalizadosTableRow,
              backgroundColor:
                pedidoActivo?.id === p.id ? (isDark() ? '#1e3a5f' : '#E8F0FE') : 'transparent',
            }}
          >
            <span
              style={{
                ...s.finalizadosCell,
                width: '60px',
                fontWeight: 700,
                color: 'var(--agx-pri)',
              }}
            >
              #{p.id}
            </span>
            <span style={{ ...s.finalizadosCell, flex: 1, minWidth: '130px' }}>
              {v('vis.pedidos.datos_cliente') ? p.cliente || 'N/A' : ''}
            </span>
            <span style={{ ...s.finalizadosCell, width: '120px', color: 'var(--agx-muted)' }}>
              {p.sede_nombre || p.codigo_sede || 'N/A'}
            </span>
            <span
              style={{
                ...s.finalizadosCell,
                width: '160px',
                color: 'var(--agx-muted)',
                fontSize: '11px',
              }}
            >
              {formatearFecha(p.fechapedido)}
            </span>
            {v('vis.pedidos.total') && (
              <span
                style={{
                  ...s.finalizadosCell,
                  width: '100px',
                  fontWeight: 600,
                  color: 'var(--agx-pri2)',
                }}
              >
                ${typeof p.total === 'number' ? fmtMonto(p.total) : p.total}
              </span>
            )}
            <span style={{ ...s.finalizadosCell, width: '110px' }}>
              <span
                style={{
                  ...s.finalizadosBadge,
                  backgroundColor:
                    (ESTADOS_COLOR[p.estado] || '#9e9e9e') + (isDark() ? '40' : '20'),
                  color: isDark() ? '#e2e8f0' : ESTADOS_COLOR[p.estado] || '#9e9e9e',
                }}
              >
                {ESTADOS_LABEL[p.estado] || p.estado}
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PedidosFinalizados;
